import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ChevronUp, ChevronDown, ZoomIn, ZoomOut, Mail } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";

import SIZE_CHART_M_URL from "../../assets/image/NEW-MONSINI-SIZE-CHART.png";
import SIZE_CHART_B_URL from "../../assets/image/Enchanted-BRIDAL-SIZE-CHART-scaled.jpg";
import SIZE_CHART_E_URL from "../../assets/image/Enchanted-MOB-SIZE-CHART-scaled.jpg";

// The base URL for the server to construct the full image path
const SERVER_BASE_URL = "https://admin.monsinidress.com";

// --- DUMMY DATA ---
const dummyData = {
	Prom: {
		years: [],
		sizeChartImage: SIZE_CHART_M_URL,
	},
	Evening: {
		years: [],
		sizeChartImage: SIZE_CHART_E_URL,
	},
	Bridal: {
		years: [],
		sizeChartImage: SIZE_CHART_B_URL,
	},
};

export default function Footer() {
	const [openMenu, setOpenMenu] = useState(null);
	const [showSizeChart, setShowSizeChart] = useState("");
	const [zoomed, setZoomed] = useState(false);
	const [footerData, setFooterData] = useState({});
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [subscribeMessage, setSubscribeMessage] = useState(null);
	const [capVal, setCapVal] = useState(null);
	const [status, setStatus] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);

	const imageRef = useRef(null);

	// This function constructs the full image URL
	function getFullImageUrl(imagePath) {
		if (!imagePath) return "";

		// Ensure path starts correctly and combine with base URL
		const cleanPath = imagePath.replace(/^uploads\//i, "uploads/");
		return `${SERVER_BASE_URL}/${cleanPath}`;
	}

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				const response = await fetch(
					`https://admin.monsinidress.com/api/navbar`,
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ brand: "Monsini" }),
					}
				);

				if (!response.ok) {
					throw new Error("Failed to fetch products");
				}
				const productsData = await response.json();

				const rawData = productsData.data || {};
				const formattedData = {};

				// Extract both years AND sizeChartImage from API data
				Object.keys(rawData).forEach((category) => {
					const categoryData = rawData[category];
					const sortedYears = categoryData?.years
						? [...categoryData.years].sort((a, b) => b - a)
						: [];
					const sizeChartImage = categoryData?.sizeChartImage || "";

					formattedData[category] = {
						years: sortedYears,
						sizeChartImage: sizeChartImage,
					};
				});

				// If API returns less than 3 categories, supplement with dummy data
				const apiCategories = Object.keys(formattedData);
				if (apiCategories.length < 3) {
					// Create a merged data object
					const mergedData = { ...formattedData };
					const dummyCategories = Object.keys(dummyData);

					// Add dummy categories until we have at least 3
					for (const category of dummyCategories) {
						if (Object.keys(mergedData).length >= 3) break;
						if (!mergedData[category]) {
							mergedData[category] = dummyData[category];
						}
					}

					setFooterData(mergedData);
				} else {
					setFooterData(formattedData);
				}
			} catch (err) {
				console.error("Error fetching footer data:", err);
				// Use dummy data as fallback on error
				setFooterData(dummyData);
			}
		};

		fetchProducts();
	}, []);

	const toggleMenu = (menu) => {
		setOpenMenu(openMenu === menu ? null : menu);
	};

	const handleSizeChart = (category) => {
		setShowSizeChart(category);
	};

	const getSizeChartImage = () => {
		console.log("Getting size chart for:", showSizeChart);
		console.log("Footer data:", footerData);

		if (showSizeChart && footerData[showSizeChart]) {
			const categoryData = footerData[showSizeChart];

			// Check if API provided a size chart image
			if (categoryData.sizeChartImage) {
				console.log("Using API size chart:", categoryData.sizeChartImage);
				// Check if it's a local path (needs server URL) or already a full URL
				if (categoryData.sizeChartImage.startsWith("uploads/")) {
					return getFullImageUrl(categoryData.sizeChartImage);
				}
				return categoryData.sizeChartImage;
			}
		}

		// Fallback to local images if no API image
		console.log("Falling back to local images");
		switch (showSizeChart) {
			case "Prom":
				return SIZE_CHART_M_URL;
			case "Bridal":
				return SIZE_CHART_B_URL;
			case "Evening":
				return SIZE_CHART_E_URL;
			default:
				return "";
		}
	};

	const getDropdownClasses = (menuName) => {
		const baseClasses =
			"ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ease-in-out";
		const isOpen = openMenu === menuName;
		const heightClass = isOpen ? "max-h-60 opacity-100" : "max-h-0 opacity-0";
		return `${baseClasses} ${heightClass}`;
	};

	const handleSubscribe = async () => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		setEmailError("");
		setSubscribeMessage(null);
		setStatus(null);

		if (!email) {
			setEmailError("Email is required");
			return;
		} else if (!emailRegex.test(email)) {
			setEmailError("Please enter a valid email address");
			return;
		}

		if (!showRecaptcha) {
			setShowRecaptcha(true);
			return;
		}

		if (!capVal) {
			setStatus("recaptcha-error");
			return;
		}

		try {
			const payload = {
				brandName: "Monsini",
				email: email,
			};

			const response = await fetch(
				`https://admin.monsinidress.com/api/subscriber`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify(payload),
				}
			);

			if (!response.ok) {
				const errorData = await response
					.json()
					.catch(() => ({ message: "Subscription failed." }));
				throw new Error(
					errorData.message || "Subscription failed. Please try again."
				);
			}

			setEmailError("");
			setSubscribeMessage("Thank you for subscribing! Check your inbox.");
			setEmail("");
			setCapVal(null);
			setShowRecaptcha(false);
			setStatus(null);
			setTimeout(() => setSubscribeMessage(null), 4000);
		} catch (error) {
			console.error("Subscription API Error:", error);
			setSubscribeMessage(
				error.message || "An unknown error occurred during subscription."
			);
			setTimeout(() => setSubscribeMessage(null), 5000);
		}
	};

	return (
		<>
			{/* 🧭 SIZE CHART MODAL */}
			{showSizeChart && (
				<div className="fixed inset-0 h-screen bg-black bg-opacity-70 flex items-center justify-center z-[1200] p-4">
					<div className="absolute top-3 right-3 z-[1201]">
						<button
							onClick={() => setShowSizeChart("")}
							className="text-gray-200 hover:text-pink-500 transition">
							<X className="w-7 h-7" />
						</button>
					</div>
					<div className="absolute top-3 left-3 z-[1201]">
						<button
							onClick={() => setZoomed((prev) => !prev)}
							className="text-gray-200 hover:text-pink-500 transition">
							{zoomed ? (
								<ZoomOut className="w-7 h-7" />
							) : (
								<ZoomIn className="w-7 h-7" />
							)}
						</button>
					</div>

					<div className="bg-transparent w-full flex flex-col justify-center items-center gap-5 relative">
						<div
							className={`w-full max-w-5xl h-full min-h-[90vh] overflow-hidden flex justify-center ${
								zoomed ? "cursor-grab" : ""
							}`}>
							<div
								ref={imageRef}
								className="relative overflow-hidden rounded-2xl bg-gray-50 max-w-full"
								style={{
									cursor: zoomed ? "zoom-in" : "default",
								}}>
								<img
									src={getSizeChartImage()}
									alt={`${showSizeChart} Size Chart`}
									className={`transition-transform duration-300 w-full object-contain`}
									style={{
										transform: zoomed ? `scale(2)` : `scale(1)`,
										maxWidth: "100vw",
									}}
									onError={(e) => {
										console.error(
											`Failed to load image for ${showSizeChart}:`,
											getSizeChartImage()
										);
									}}
								/>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Custom Subscription Message */}
			{subscribeMessage && (
				<div
					className={`fixed bottom-4 right-4 text-white p-3 rounded-lg shadow-xl z-[1202] flex items-center gap-2 ${
						subscribeMessage.includes("failed") ? "bg-red-600" : "bg-green-500"
					}`}>
					<Mail className="w-5 h-5" />
					{subscribeMessage}
				</div>
			)}

			{/* FOOTER */}
			<div className="w-full bg-pink-500 overflow-x-hidden">
				<div className="w-full md:px-10 px-6 lg:px-16 grid lg:grid-cols-4 md:grid-cols-2 gap-5 border-b">
					{/* Newsletter */}
					<div className="flex justify-center items-start text-white flex-col lg:px-10 md:px-4 md:py-2 lg:py-6">
						<h2 className="text-3xl font-lora my-4">Let's get in Touch</h2>
						<p className="font-poppins text-[14px]">
							Sign up for our newsletter and get news about latest products and
							discounts
						</p>

						<input
							type="text"
							placeholder="Enter Your Email Address..."
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="text-white placeholder:text-white font-poppins px-3 w-full outline-none border-2 bg-inherit text-[16px] py-2 rounded-lg my-2 border-white hover:bg-white hover:bg-opacity-25 transition-all duration-300 focus:bg-white focus:bg-opacity-25"
						/>
						{emailError && (
							<p className="text-white text-sm bg-red-600 p-1 rounded mt-1">
								{emailError}
							</p>
						)}

						{/* CORRECTED: ReCAPTCHA Integration */}
						{showRecaptcha && (
							<div>
								<ReCAPTCHA
									// sitekey="6LdmLyAsAAAAAEqPIVh3SRYMQI2sDuuPlPxgGusZ"
									sitekey="6LeKeywsAAAAAHhTtElwWALyKKJoy9uXHWODEk4f"
									//  verify site key 6LeKeywsAAAAAFHw0s_IGAxloW5qQXsdGYrk3S7j

									onChange={(val) => setCapVal(val)}
								/>
								{status === "recaptcha-error" && (
									<p className="mt-2 text-sm text-red-600 font-medium">
										Please complete the reCAPTCHA verification
									</p>
								)}
							</div>
						)}

						<button
							onClick={handleSubscribe}
							className="mt-4 bg-white text-pink-500 font-semibold py-1.5 px-4 rounded hover:bg-opacity-90 transition w-full">
							{showRecaptcha ? "Complete Subscription" : "Subscribe"}
						</button>
					</div>

					{/* Dynamic Dropdowns */}
					<div className="text-white font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Collections
						</h2>
						{Object.keys(footerData).map((cleanCategoryName) => {
							const menuKey = cleanCategoryName
								.replace(/\s+/g, "")
								.toLowerCase();
							const yearsList = footerData[cleanCategoryName].years || [];
							// const sizeChartImage =
							// 	footerData[cleanCategoryName].sizeChartImage;

							return (
								<div key={cleanCategoryName} className="font-poppins">
									<button
										onClick={() => toggleMenu(menuKey)}
										className="flex justify-between items-center w-full py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
										<span>{cleanCategoryName}</span>
										<span>
											{openMenu === menuKey ? <ChevronUp /> : <ChevronDown />}
										</span>
									</button>
									<div className={getDropdownClasses(menuKey)}>
										{yearsList.length > 0
											? yearsList.map((year) => (
													<Link
														key={year}
														to={`/${cleanCategoryName
															.split(" ")[0]
															.toLowerCase()}/${year}`}
														className="py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-3 block">
														{year} Collection
													</Link>
											  ))
											: ""}
										<button
											onClick={() => handleSizeChart(cleanCategoryName)}
											className="py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-3 w-full text-left">
											Size Chart
										</button>
									</div>
								</div>
							);
						})}
					</div>

					{/* Contact Section */}
					<div className="text-white font-Jost-Regular md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Contact Links
						</h2>
						<p className="font-poppins">
							<Link
								to="/about"
								className="block py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
								About Us
							</Link>
						</p>
						<p className="font-poppins">
							<Link
								to="/contact"
								className="block py-1 px-2 rounded transition-all duration-300 hover:bg-white hover:bg-opacity-25 hover:px-2">
								Contact Us
							</Link>
						</p>
						<p className="mt-3">
							<Link
								to="https://market.monsiniprom.com/"
								className="text-white hover:bg-white hover:bg-opacity-25 transition-colors duration-200 font-bold border border-white px-4 py-1.5 rounded-md">
								Retailer Login
							</Link>
						</p>
					</div>

					{/* Social Section */}
					<div className="text-white md:px-5 lg:px-5 flex flex-col gap-2 py-6 md:py-10 lg:py-14">
						<h2 className="font-lora mb-2 font-medium text-[20px]">
							Social Links
						</h2>
						<p className="font-poppins hover:bg-white hover:bg-opacity-25 py-1 px-2 rounded transition-all duration-300 cursor-default">
							<Link
								to={
									"https://www.instagram.com/monsiniprom?igsh=ZnRva3FtZ3czbGU5"
								}>
								Instagram
							</Link>
						</p>
					</div>
				</div>
			</div>
		</>
	);
}
